import React, { ReactElement, useEffect, useRef, useState } from 'react';
import Accordion from '../../components/accordions/accordion';
import AccordionDetails from '../../components/accordions/accordion-details';
import FilterChip from '../../components/chips/filter-chip';
import { Filter } from '../../types/filter';
import PackshotPanelHeader from './packshot-panel-header';
import PackshotAssets from './packshot-assets';
import SizeFilter from '../filter/size-filter';
import ColorFilter from '../filter/color-filter';
import useLocale from '../../hooks/use-locale';
import { useStoreValue } from '../../hooks/use-store-value';
import { TemplateObject } from '../../generated/gql/graphql';
import TemplateSelector from '../../stores/selectors/template';
import CanvasSelectors from '../../stores/selectors/canvas';
import { EditMode } from '../../types/enums/edit-mode';
import { useServices } from '../../hooks/use-services';

export default function PackshotSelect(): ReactElement {
    const [selectedSizeFilters, setSelectedSizeFilters] = useState<Filter[]>([]);
    const [selectedColorFilters, setSelectedColorFilters] = useState<Filter[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [isExpanded, setIsExpanded] = useState<boolean>(false);

    const imageProps = useStoreValue<TemplateObject[]>(TemplateSelector.getImageProperties());
    const selectedObjectId = useStoreValue<string>(CanvasSelectors.getSelectedObjectId());
    const currentEditMode = useStoreValue<EditMode>(CanvasSelectors.editMode());

    const expanded = imageProps?.some((item) => item.id === selectedObjectId) || false;

    const assetGroup = useStoreValue<string>(TemplateSelector.getImageAssetGroupById(selectedObjectId));
    const isPackshotAsset = assetGroup === 'Packshot' || assetGroup === undefined;

    const { getText } = useLocale();
    const { canvas } = useServices();

    const scrollRef = useRef<HTMLDivElement>();

    useEffect(() => {
        setIsExpanded(expanded);
    }, [expanded, currentEditMode, selectedObjectId]);

    function onExpand(expand: boolean): void {
        canvas.setEditMode(EditMode.NOT_EDITING);
        setIsExpanded(expand);
    }

    const handleDeleteAll = (): void => {
        setSelectedSizeFilters([]);
        setSelectedColorFilters([]);
    };

    function handleAddSizeFilter(filter: Filter): void {
        if (!selectedSizeFilters.some((f) => f.id === filter.id)) setSelectedSizeFilters((prev) => [...prev, filter]);
    }

    function handleDeleteSizeFilter(filterId: string): void {
        const foundFilter = selectedSizeFilters.find((f) => f.id === filterId);
        if (foundFilter) setSelectedSizeFilters(selectedSizeFilters.filter((f) => f.id !== foundFilter.id));
    }

    function handleAddColorFilter(filter: Filter): void {
        if (!selectedColorFilters.some((f) => f.id === filter.id)) setSelectedColorFilters((prev) => [...prev, filter]);
    }

    function handleDeleteColorFilter(filterId: string): void {
        const foundFilter = selectedColorFilters.find((f) => f.id === filterId);
        if (foundFilter) setSelectedColorFilters(selectedColorFilters.filter((f) => f.id !== foundFilter.id));
    }

    function handleSelectAllSizeFilter(filters: Filter[]): void {
        setSelectedSizeFilters(filters);
    }

    function handleSelectAllColorFilters(filters: Filter[]): void {
        setSelectedColorFilters(filters);
    }

    function handleSearchInput(searchInput: string): void {
        setSearchTerm(searchInput);
    }

    const showPackshotFiltersDebug = false;

    return (
        <Accordion
            label={getText('widgets.packshotSelect.label')}
            summaryChildren={<PackshotPanelHeader onSearch={(input) => handleSearchInput(input)} />}
            expanded={isExpanded && currentEditMode === EditMode.NOT_EDITING && isPackshotAsset}
            onExpand={(e) => onExpand(e)}
        >
            <AccordionDetails ref={scrollRef}>
                {showPackshotFiltersDebug && (
                    <div className='mb-10 flex flex-wrap gap-10'>
                        <SizeFilter
                            activeFilterIds={selectedSizeFilters.map((filter) => filter.id)}
                            onAdd={(filter) => handleAddSizeFilter(filter)}
                            onDelete={(id) => handleDeleteSizeFilter(id)}
                            onDeleteAll={() => {
                                setSelectedSizeFilters([]);
                            }}
                            onAddAll={(filters) => {
                                handleSelectAllSizeFilter(filters);
                            }}
                        />
                        <ColorFilter
                            activeFilterIds={selectedColorFilters.map((filter) => filter.id)}
                            onAdd={(filter) => handleAddColorFilter(filter)}
                            onDelete={(id) => handleDeleteColorFilter(id)}
                            onDeleteAll={() => {
                                setSelectedColorFilters([]);
                            }}
                            onAddAll={(filters) => {
                                handleSelectAllColorFilters(filters);
                            }}
                        />
                    </div>
                )}

                <div className='flex flex-wrap gap-10'>
                    {selectedSizeFilters.map((filter) => (
                        <FilterChip
                            key={filter.id}
                            label={filter.name}
                            id={filter.id}
                            count={filter.count}
                            onDelete={(id) => handleDeleteSizeFilter(id)}
                        />
                    ))}
                    {selectedColorFilters.map((filter) => (
                        <FilterChip
                            key={filter.id}
                            label={filter.name}
                            id={filter.id}
                            count={filter.count}
                            onDelete={(id) => handleDeleteColorFilter(id)}
                        />
                    ))}
                    {selectedSizeFilters.length > 0 || selectedColorFilters.length > 0 ? (
                        <button type='button' className='text-base text-companyMediumGrey' onClick={handleDeleteAll}>
                            {getText('widgets.backgroundSelect.deleteAllFilter')}
                        </button>
                    ) : undefined}
                </div>
                <PackshotAssets
                    filterIds={[
                        ...selectedSizeFilters.map((filter) => filter.id),
                        ...selectedColorFilters.map((filter) => filter.id),
                    ]}
                    searchTerm={searchTerm}
                    scrollRef={scrollRef}
                />
            </AccordionDetails>
        </Accordion>
    );
}
